import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Brain,
  Castle,
  FileText,
  Globe,
  Inbox,
  Lightbulb,
  Link2,
  ListChecks,
  Package,
  Stethoscope,
  Sun,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Aviso } from '@/components/ui/aviso';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/app-layout';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { pedirSalida } from '@/lib/salida-con-cambios';
import ReglasPage from '@/features/pricing-rules/pages/reglas-page';
import { usePermisosDeReglas } from '@/features/pricing-rules/lib/use-permisos-de-reglas';
import { LinkedProductsView } from '../components/linked-products-view';
import { MarketOverviewTab } from '../components/market-overview-tab';
import { CatalogDiagnosisTab } from '../components/catalog-diagnosis-tab';
import { StrategyEffectivenessView } from '../components/strategy-effectiveness-view';
import { SignalsTab } from '../components/signals-tab';
import { StrategicReportTab } from '../components/strategic-report-tab';
import { ProposalsInboxTab } from '../components/proposals-inbox-tab';
import { AppliedStrategiesTab } from '../components/applied-strategies-tab';
import { HoyTab } from '../components/hoy-tab';
import { VueltaSheet } from '../components/vuelta-sheet';
import { DestinoProductos } from '../components/destino-productos';
import { MOSTRAR_EN_DESARROLLO, PESTANA_ANALISIS_VISIBLE } from '../lib/banderas';
import { useCambiosNuevos } from '../hooks/use-cambios-nuevos';
import type { DestinoHoy, FiltroBandeja } from '../types/price-proposals.types';

/** Los destinos que «Recargar» vuelve a pedir (el `refreshKey`). */
const DESTINOS_QUE_RECARGAN: readonly string[] = ['hoy', 'bandeja', 'estrategias'];

/**
 * Los seis destinos del módulo (plan 3, spec 212 §6). Los cuatro últimos sólo
 * existen con la bandera interna: están a medio terminar, no se borran.
 * «Análisis» depende de `PESTANA_ANALISIS_VISIBLE` (escondida desde el 7-oct).
 */
const DESTINOS = [
  'hoy',
  'bandeja',
  'productos',
  'estrategias',
  'reglas',
  'analisis',
  'insights',
  'informe',
  'diagnostico',
  'vinculados',
] as const;
type Destino = (typeof DESTINOS)[number];

const esDestino = (v: string | null): v is Destino =>
  v != null && (DESTINOS as readonly string[]).includes(v);

export default function SmartPricingPage() {
  // Reglas se ve con el permiso de ver reglas (decisión del 26-sep: cinco
  // permisos por rol, separados de Precios Dinámicos; cualquiera de escritura
  // implica ver). Qué botones ve cada uno lo resuelve ReglasPage sola (4B T3).
  // Productos lee la foto del catálogo (`GET /api/pricing-rules/foto`), que el
  // backend guarda con el mismo permiso (4B-datos, Tarea 1b): sin él, no se ofrece.
  const { ver: veReglas } = usePermisosDeReglas();
  // P3-D10 (27-sep): quien ve reglas entra aunque no tenga Estrategias de
  // Mercado (`menu:data-import`, Tarea 1b); entonces sólo ve Reglas y Productos.
  // Escaneo previo Nº 7: si no sabemos qué módulos tiene (sesión sin `menuItems`),
  // no se bloquea — la misma regla que `ProtectedRoute`. `canSeeMenu` sola tiene
  // un respaldo restrictivo y dejaría la página sin ninguna pestaña.
  const { canSeeMenu, menuItems, isModuleLocked } = useAuth();
  // Re-auditoría del 27-sep noche (A1): un módulo «Bloqueado» para el rol está en
  // `menuItems` (se ve con candado) y `canSeeMenu` da true. Desde la T1b la guarda
  // no lo corta a quien ve reglas: lo corta la página.
  const veEstrategias =
    !isModuleLocked('data-import') && (menuItems.length === 0 || canSeeMenu('data-import'));

  // `?destino=reglas` lo usa la redirección de /pricing-rules; un enlace a un
  // destino escondido (o sin permiso) cae en el de siempre.
  const [params, setParams] = useSearchParams();
  const pedido = params.get('destino');
  // Plan 2 (la vuelta): la hoja de una estrategia vencida. Se abre desde Hoy,
  // desde el aviso (`?vuelta=<id>`) o desde una vuelta bloqueada de Por decidir
  // (P2-D6). Este archivo se reemplaza entero: no perderla.
  const vuelta = params.get('vuelta');
  // `prev` y no `params`: dos cambios en el mismo clic no se pisan (plan 2 T5).
  const abrirVuelta = (id: string | null) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (id) p.set('vuelta', id);
        else p.delete('vuelta');
        return p;
      },
      { replace: true }
    );
  const disponible = (d: Destino) =>
    d === 'reglas' || d === 'productos'
      ? veReglas
      : d === 'analisis'
        ? PESTANA_ANALISIS_VISIBLE && veEstrategias
        : ['insights', 'informe', 'diagnostico', 'vinculados'].includes(d)
          ? MOSTRAR_EN_DESARROLLO && veEstrategias
          : veEstrategias;

  // Hoy arranca abierta a propósito: la pantalla se organiza por decisión
  // pendiente (spec 156, decisión #6), y lo primero es qué hay que decidir hoy.
  // Sin Estrategias (P3-D10) se arranca en Reglas, que es a lo que vino.
  const porOmision: Destino = veEstrategias ? 'hoy' : 'reglas';
  const [destino, setDestino] = useState<Destino>(
    esDestino(pedido) && disponible(pedido) ? pedido : porOmision
  );
  // Se llenan cuando se entra a Por decidir desde una decisión de Hoy.
  const [filtroEstrategia, setFiltroEstrategia] = useState<string | null>(null);
  // Plan 2 (revisión final I3): sube cuando se resuelve algo en la hoja de la
  // vuelta, y Hoy y Por decidir se vuelven a cargar. Este archivo se reemplaza
  // entero: no perderlo (las props son opcionales y tsc no avisa).
  const [refreshKey, setRefreshKey] = useState(0);
  // Spec 275 (R3, D-R3-2): «Hay cambios nuevos» cuando otra persona o una
  // corrida cambian algo; `aceptar` es el «Recargar» de la franja.
  const cambios = useCambiosNuevos();
  // Lote C2b: «Ver en Por decidir» de la ficha llega con `?producto=<id>`.
  const [filtroBandeja, setFiltroBandeja] = useState<FiltroBandeja | null>(() => {
    const producto = params.get('producto');
    return producto && pedido === 'bandeja' ? { productId: producto } : null;
  });

  const cambiar = (d: Destino) => {
    setDestino(d);
    // La URL dice dónde se está: recargar o compartir el enlace vuelve acá.
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('destino', d);
        // Ir a otro destino cierra la hoja de la vuelta (plan 2): un solo
        // setParams, así no se pisan dos cambios de la URL en el mismo clic.
        p.delete('vuelta');
        // 27-sep (H4): `?ver=sin-costo` sirve para llegar filtrado desde Hoy una
        // vez. Si queda, volver a Productos lo abre filtrado otra vez (Radix
        // desmonta la pestaña y DestinoProductos lee `ver` al montarse).
        p.delete('ver');
        // Lote C2b: `?producto=` sirve para llegar una vez, como `ver`.
        p.delete('producto');
        return p;
      },
      { replace: true }
    );
  };

  /**
   * Una decisión de Hoy lleva al lugar donde se decide con el contexto completo.
   * Las propuestas van a Por decidir ya filtrado; una corrida fallida, a
   * Estrategias.
   */
  const irA = (a: DestinoHoy, strategyApplicationId: string | null, filtro?: FiltroBandeja) => {
    // Plan 2: la vuelta abre su hoja encima del destino en que se está.
    if (a === 'VUELTA') {
      abrirVuelta(strategyApplicationId);
      return;
    }
    if (a === 'BANDEJA') {
      setFiltroEstrategia(strategyApplicationId);
      setFiltroBandeja(filtro ?? null);
      cambiar('bandeja');
      return;
    }
    cambiar('estrategias');
  };

  return (
    <AppLayout>
      <div className="space-y-6 p-6">
        <div className="flex items-center gap-3">
          {/* El azul de la marca, sin el degradado a violeta (mapa 226, decisión 3):
              el violeta queda sólo para lo que escribe la IA. `primary` tiene su
              pareja oscura; el `prixia-ink` no. */}
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
            <Brain className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <div className="mb-3">
              <EtiquetaDePagina modulo="compite">Compite</EtiquetaDePagina>
            </div>
            <h1 className={CLASE_TITULO_DE_PAGINA}>Estrategias de Mercado</h1>
            <p className="text-sm text-muted-foreground">
              Qué hay que decidir hoy, qué proponen tus estrategias y qué las limita
            </p>
          </div>
        </div>

        <Tabs
          value={destino}
          onValueChange={(v) => {
            if (!esDestino(v)) return;
            // Spec 294 (decisión del dueño, 7-oct): salir de Reglas con cambios
            // propios sin aplicar pregunta antes (la guardia la pone Reglas mientras
            // está abierta; sin ella, sigue de una). `irA` no pregunta: desde Reglas
            // sólo se usa después de aplicar.
            pedirSalida(() => {
              // `ec289f8f` (26-sep): sólo se llega acá apretando la pestaña a mano (Hoy
              // navega con `irA`): el filtro que trajo Hoy no se queda pegado. Va acá y
              // no en `cambiar`, que también usa `irA`.
              if (v === 'bandeja') setFiltroBandeja(null);
              cambiar(v);
            });
          }}
        >
          <TabsList>
            {veEstrategias && (
              <>
                <TabsTrigger value="hoy" className="gap-1.5">
                  <Sun className="h-4 w-4" />
                  Hoy
                </TabsTrigger>
                <TabsTrigger value="bandeja" className="gap-1.5">
                  <Inbox className="h-4 w-4" />
                  Por decidir
                </TabsTrigger>
              </>
            )}
            {veReglas && (
              <TabsTrigger value="productos" className="gap-1.5">
                <Package className="h-4 w-4" />
                Productos
              </TabsTrigger>
            )}
            {veEstrategias && (
              <TabsTrigger value="estrategias" className="gap-1.5">
                <Castle className="h-4 w-4" />
                Estrategias
              </TabsTrigger>
            )}
            {veReglas && (
              <TabsTrigger value="reglas" className="gap-1.5">
                <ListChecks className="h-4 w-4" />
                Reglas
              </TabsTrigger>
            )}
            {PESTANA_ANALISIS_VISIBLE && veEstrategias && (
              <TabsTrigger value="analisis" className="gap-1.5">
                <Globe className="h-4 w-4" />
                Análisis
              </TabsTrigger>
            )}
            {MOSTRAR_EN_DESARROLLO && veEstrategias && (
              <>
                <TabsTrigger value="insights" className="gap-1.5">
                  <Lightbulb className="h-4 w-4" />
                  Insights
                </TabsTrigger>
                <TabsTrigger value="informe" className="gap-1.5">
                  <FileText className="h-4 w-4" />
                  Informe IA
                </TabsTrigger>
                <TabsTrigger value="diagnostico" className="gap-1.5">
                  <Stethoscope className="h-4 w-4" />
                  Diagnóstico de Catálogo
                </TabsTrigger>
                <TabsTrigger value="vinculados" className="gap-1.5">
                  <Link2 className="h-4 w-4" />
                  Productos vinculados
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Spec 275 (R3, D-R3-3): avisa, no recarga solo. Revisión final de rama
              (F2): sólo donde «Recargar» recarga algo (Hoy, Por decidir y
              Estrategias); en las otras pestañas el aviso se guarda y vuelve. */}
          {cambios.hay && veEstrategias && DESTINOS_QUE_RECARGAN.includes(destino) && (
            <Aviso
              tono="info"
              role="status"
              data-testid="hay-cambios-nuevos"
              className="mt-3 items-center"
            >
              <span className="flex flex-wrap items-center gap-2">
                Hay cambios nuevos ·
                <Button
                  size="sm"
                  variant="link"
                  className="h-auto p-0"
                  onClick={() => {
                    setRefreshKey((k) => k + 1);
                    cambios.aceptar();
                  }}
                >
                  Recargar
                </Button>
              </span>
            </Aviso>
          )}

          {veEstrategias && (
            <>
              <TabsContent value="hoy" className="mt-6">
                <HoyTab onIrA={irA} refreshKey={refreshKey} />
              </TabsContent>

              <TabsContent value="bandeja" className="mt-6">
                <ProposalsInboxTab
                  filtroEstrategiaInicial={filtroEstrategia}
                  filtroInicial={filtroBandeja}
                  // Plan 2, P2-D6: una vuelta bloqueada manda a su hoja. La prop es
                  // opcional: si se pierde, el botón desaparece sin error (el test lo mira).
                  onAbrirVuelta={(id) => abrirVuelta(id)}
                  refreshKey={refreshKey}
                />
              </TabsContent>
            </>
          )}

          {veReglas && (
            <TabsContent value="productos" className="mt-6">
              <DestinoProductos />
            </TabsContent>
          )}

          {veEstrategias && (
            <TabsContent value="estrategias" className="mt-6">
              {/* Auditoría I1: «N esperan tu decisión» abre Por decidir filtrado por esa estrategia. */}
              <AppliedStrategiesTab onIrA={irA} refreshKey={refreshKey} />
            </TabsContent>
          )}

          {veReglas && (
            <TabsContent value="reglas" className="mt-6">
              <ReglasPage
                embebida
                // Lote C1: después de recalcular, Por decidir. Spec 294 (D-R4-3),
                // decisión del dueño (7-oct): sin filtro de origen.
                // «Origen: recálculo» ya trae sólo las sin estrategia, y escondería
                // todo lo que el recálculo reescribió de las estrategias.
                onIrAPorDecidirDelRecalculo={() => irA('BANDEJA', null)}
              />
            </TabsContent>
          )}

          {/* Los alcances por posición de precio y por categoría (StrategyGroupsView)
              ya viven adentro de MarketOverviewTab: no se montan dos veces. */}
          {PESTANA_ANALISIS_VISIBLE && veEstrategias && (
            <TabsContent value="analisis" className="mt-6">
              <MarketOverviewTab />
            </TabsContent>
          )}

          {MOSTRAR_EN_DESARROLLO && veEstrategias && (
            <>
              <TabsContent value="insights" className="mt-6">
                <div className="space-y-8">
                  <SignalsTab />
                  <StrategyEffectivenessView />
                </div>
              </TabsContent>
              <TabsContent value="informe" className="mt-6">
                <StrategicReportTab />
              </TabsContent>
              <TabsContent value="diagnostico" className="mt-6">
                <CatalogDiagnosisTab />
              </TabsContent>
              <TabsContent value="vinculados" className="mt-6">
                <LinkedProductsView />
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
      {/* Sin Estrategias (P3-D10) no hay vuelta que mirar: la hoja pediría la bandeja. */}
      {veEstrategias && (
        <VueltaSheet
          strategyApplicationId={vuelta}
          onClose={() => abrirVuelta(null)}
          onIrA={irA}
          onResuelto={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </AppLayout>
  );
}
